import i18n from './i18n';
import { AxiosError } from 'axios';
function translateFieldName(fieldName: string): string {
  const fieldTranslations: Record<string, string> = {
    name: i18n.t('settings.roles.name', { defaultValue: 'Name' }),
    description: i18n.t('settings.roles.description', { defaultValue: 'Description' }),
  };
  const translated = fieldTranslations[fieldName.toLowerCase()];
  return translated || fieldName;
}
function parseValidationMessage(message: string): {
  key: string;
  params: Record<string, any>;
} | null {
  const minLengthMatch = message.match(/^String must contain at least (\d+) character/i);
  if (minLengthMatch) {
    return {
      key: 'MESSAGES.VALIDATION.MIN_LENGTH',
      params: { field: '', limit: minLengthMatch[1] },
    };
  }
  const maxLengthMatch = message.match(/^String must contain at most (\d+) character/i);
  if (maxLengthMatch) {
    return {
      key: 'MESSAGES.VALIDATION.MAX_LENGTH',
      params: { field: '', limit: maxLengthMatch[1] },
    };
  }
  const requiredMatch = message.match(/^Required$/i);
  if (requiredMatch) {
    return {
      key: 'MESSAGES.VALIDATION.REQUIRED',
      params: { field: '' },
    };
  }
  return null;
}
export function translateErrorMessage(
  errorMessage: string | undefined | null,
  params?: Record<string, string | number | any>
): string {
  if (params?.resource && typeof params.resource === 'string') {
    const parsed = parseValidationMessage(params.resource);
    if (parsed) {
      const translated = i18n.t(parsed.key, parsed.params);
      if (translated !== parsed.key) {
        return String(translated);
      }
    }
    return params.resource;
  }
  if (!errorMessage) {
    return String(i18n.t('MESSAGES.GENERAL.INTERNAL_SERVER_ERROR'));
  }
  if (errorMessage.startsWith('MESSAGES.')) {
    let translationKey = errorMessage;
    let translationParams: Record<string, any> = { ...(params || {}) };
    // Subscription tier errors carry a moduleKey (e.g. "groups"); resolve it to the
    // localized feature name ("Gruplar") so the message can say which feature is
    // missing. If the key is unknown (drift/typo), drop to the *_GENERIC variant
    // rather than interpolating a raw key into the sentence.
    if (typeof translationParams.moduleKey === 'string') {
      const moduleNameKey = `packageManagementPage.moduleNames.${translationParams.moduleKey}`;
      const moduleName = i18n.t(moduleNameKey);
      if (moduleName && moduleName !== moduleNameKey) {
        translationParams.module = moduleName;
      } else {
        translationKey = `${translationKey}_GENERIC`;
      }
    }
    const translated = i18n.t(translationKey, translationParams);
    if (translated === translationKey) {
      return String(i18n.t('MESSAGES.GENERAL.INTERNAL_SERVER_ERROR'));
    }
    return String(translated);
  }
  const parsed = parseValidationMessage(errorMessage);
  if (parsed) {
    const translated = i18n.t(parsed.key, parsed.params);
    if (translated !== parsed.key) {
      return String(translated);
    }
  }
  return errorMessage;
}
export function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const responseData = error?.response?.data;
    const rawErrorMessage =
      responseData?.message ||
      responseData?.error?.message ||
      error?.message ||
      'MESSAGES.GENERAL.INTERNAL_SERVER_ERROR';
    const errorParams = responseData?.params || responseData?.error?.params || {};
    return translateErrorMessage(rawErrorMessage, errorParams);
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(i18n.t('MESSAGES.GENERAL.INTERNAL_SERVER_ERROR'));
}
